/**
 * Notificações do navegador e aviso sonoro para o WebChat.
 * Solicita permissão ao usuário e exibe notificação + som quando chega nova mensagem.
 * O som só funciona após um gesto do usuário (ex.: abrir o chat) por política de autoplay.
 * Apenas uma notificação (som + tela) por mensagem, deduplicada por messageId.
 */

let permissionRequested = false;
let sharedAudioContext: AudioContext | null = null;
const notifiedMessageIds = new Set<string>();
const MAX_NOTIFIED_IDS = 200;

function wasNotifiedForMessage(messageId: string): boolean {
  if (!messageId) return false;
  return notifiedMessageIds.has(messageId);
}

function markMessageAsNotified(messageId: string): void {
  if (!messageId) return;
  notifiedMessageIds.add(messageId);
  if (notifiedMessageIds.size > MAX_NOTIFIED_IDS) {
    const arr = Array.from(notifiedMessageIds);
    arr.slice(0, 100).forEach((id) => notifiedMessageIds.delete(id));
  }
}

export function requestNotificationPermission(): Promise<void> {
  if (typeof window === "undefined" || typeof Notification === "undefined")
    return Promise.resolve();
  if (permissionRequested) return Promise.resolve();
  permissionRequested = true;
  if (Notification.permission === "granted") return Promise.resolve();
  if (Notification.permission === "denied") return Promise.resolve();
  return Notification.requestPermission().catch(() => {});
}

/**
 * Deve ser chamado em um gesto do usuário (ex.: ao abrir o chat) para liberar o áudio.
 * Navegadores bloqueiam som disparado por eventos (SSE) sem interação prévia.
 */
export function prepareNotificationSound(): void {
  if (typeof window === "undefined") return;
  const C = window.AudioContext || (window as any).webkitAudioContext;
  if (!C) return;
  try {
    if (!sharedAudioContext) sharedAudioContext = new C();
    if (sharedAudioContext.state === "suspended") sharedAudioContext.resume();
  } catch {
    // ignore
  }
}

export function playNotificationSound(): void {
  try {
    const C =
      typeof window !== "undefined" &&
      (window.AudioContext || (window as any).webkitAudioContext);
    if (!C) return;
    const ctx = sharedAudioContext || new (C as typeof AudioContext)();
    if (ctx.state === "suspended") ctx.resume().then(() => playBeep(ctx)).catch(() => playBeep(ctx));
    else playBeep(ctx);
  } catch {
    // ignore
  }
}

function playBeep(ctx: AudioContext): void {
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    osc.type = "sine";
    gain.gain.setValueAtTime(0.2, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.2);
  } catch {
    // ignore
  }
}

export function showBrowserNotification(
  title: string,
  body: string,
  onClick?: () => void
): void {
  if (
    typeof Notification === "undefined" ||
    Notification.permission !== "granted"
  )
    return;
  try {
    const n = new Notification(title, { body });
    n.onclick = () => {
      n.close();
      if (window.focus) window.focus();
      onClick?.();
    };
    setTimeout(() => n.close(), 6000);
  } catch {
    // ignore
  }
}

/**
 * Dispara uma única notificação (som + notificação em tela) por mensagem.
 * Se messageId for passado e já tiver sido notificado para esse id, não faz nada.
 */
export function notifyNewMessage(
  content: string,
  title = "WebChat – Nova mensagem",
  onNotificationClick?: () => void,
  messageId?: string
): void {
  if (messageId && wasNotifiedForMessage(messageId)) return;
  if (messageId) markMessageAsNotified(messageId);

  requestNotificationPermission().then(() => {
    playNotificationSound();
    const body =
      (content || "").trim().length > 80
        ? (content || "").trim().slice(0, 77) + "..."
        : (content || "").trim() || "Você recebeu uma nova mensagem.";
    showBrowserNotification(title, body, onNotificationClick);
  });
}
